import React from 'react';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { format } from 'date-fns';
import HelpIcon from '../Common/HelpIcon';
import './LineChart.css';

interface ComboChartProps {
  data: Array<{
    date: string;
    impressions: number;
    clicks?: number;
  }>;
  color?: string;
  clicksColor?: string;
  title?: string;
  height?: number;
  showClicks?: boolean;
  onHelpClick?: () => void;
  isLoading?: boolean;  // Phase 2.1.3: Loading state
  error?: string | null; // Phase 2.1.3: Error state
  onRetry?: () => void;  // Phase 2.1.3: Retry callback
}

// Phase 1.3: Memoized to prevent unnecessary re-renders on parent updates
const ComboChart: React.FC<ComboChartProps> = React.memo(({
  data,
  color = '#DA291C',
  clicksColor = '#2C3E50',
  title,
  height = 300,
  showClicks = true,
  onHelpClick,
  isLoading = false,
  error = null,
  onRetry
}) => {
  const formatXAxis = (tickItem: string) => {
    if (!tickItem || typeof tickItem !== 'string') return '';
    try {
      // Parse as local midnight (not UTC) so timezone offsets don't shift the month label.
      // new Date("YYYY-MM-DD") is UTC midnight; new Date(y, m-1, d) is local midnight.
      const parts = (tickItem.length === 7 ? tickItem + '-01' : tickItem).split('-');
      const date = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
      return format(date, 'MMM yyyy');
    } catch {
      return tickItem;
    }
  };

  const formatTooltipValue = (value: number) => {
    return value.toLocaleString();
  };

  // Phase 2.1.3: Show loading skeleton
  if (isLoading) {
    return (
      <div className="line-chart-container">
        {title && (
          <h3 className="line-chart-title">
            {title}
            {onHelpClick && <HelpIcon onClick={onHelpClick} className="chart-title" />}
          </h3>
        )}
        <div className="chart-loading-skeleton" style={{ height: `${height}px` }}>
          <div className="skeleton-bars">
            {[...Array(10)].map((_, i) => (
              <div
                key={i}
                className="skeleton-bar"
                style={{
                  height: `${Math.random() * 60 + 40}%`,
                  backgroundColor: `${color}40`
                }}
              />
            ))}
          </div>
          <div className="skeleton-spinner"></div>
        </div>
      </div>
    );
  }

  // Phase 2.1.3: Show error state with retry
  if (error) {
    return (
      <div className="line-chart-container">
        {title && (
          <h3 className="line-chart-title">
            {title}
            {onHelpClick && <HelpIcon onClick={onHelpClick} className="chart-title" />}
          </h3>
        )}
        <div className="chart-error" style={{ height: `${height}px` }}>
          <div className="error-content">
            <svg className="error-icon" viewBox="0 0 24 24" width="48" height="48">
              <path fill="#DA291C" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
            </svg>
            <p className="error-message">{error}</p>
            {onRetry && (
              <button className="retry-button" onClick={onRetry}>
                Retry
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Phase 2.1.3: Show empty state if no data
  if (!data || data.length === 0) {
    return (
      <div className="line-chart-container">
        {title && (
          <h3 className="line-chart-title">
            {title}
            {onHelpClick && <HelpIcon onClick={onHelpClick} className="chart-title" />}
          </h3>
        )}
        <div className="chart-empty" style={{ height: `${height}px` }}>
          <div className="empty-content">
            <svg className="empty-icon" viewBox="0 0 24 24" width="48" height="48">
              <path fill="#9E9E9E" d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z"/>
            </svg>
            <p className="empty-message">No data available for this time period</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="line-chart-container">
      {title && (
        <h3 className="line-chart-title">
          {title}
          {onHelpClick && <HelpIcon onClick={onHelpClick} className="chart-title" />}
        </h3>
      )}
      <ResponsiveContainer width="100%" height={height}>
        <ComposedChart
          data={data}
          margin={{ top: 5, right: 50, left: 20, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#B9B9B9" />
          <XAxis
            dataKey="date"
            tickFormatter={formatXAxis}
            stroke="#616161"
            style={{ fontSize: '12px' }}
          />
          {/* Left Y-axis for Impressions (bars) */}
          <YAxis
            yAxisId="left"
            stroke="#616161"
            style={{ fontSize: '12px' }}
            tickFormatter={(value) => value.toLocaleString()}
            label={{ value: 'Impressions', angle: -90, position: 'insideLeft', style: { fontSize: '12px' } }}
          />
          {/* Right Y-axis for Clicks (line) */}
          {showClicks && (
            <YAxis
              yAxisId="right"
              orientation="right"
              stroke={clicksColor}
              style={{ fontSize: '12px' }}
              tickFormatter={(value) => value.toLocaleString()}
              label={{ value: 'Clicks', angle: 90, position: 'insideRight', style: { fontSize: '12px', fill: clicksColor } }}
            />
          )}
          <Tooltip
            formatter={formatTooltipValue}
            labelFormatter={(label) => {
              if (!label || typeof label !== 'string') return '';
              try {
                const parts = (label.length === 7 ? label + '-01' : label).split('-');
                const date = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
                return format(date, 'MMM yyyy');
              } catch {
                return label;
              }
            }}
            contentStyle={{
              backgroundColor: '#fff',
              border: '1px solid #B9B9B9',
              borderRadius: '4px'
            }}
          />
          <Legend />

          {/* Bar chart for Impressions */}
          <Bar
            yAxisId="left"
            dataKey="impressions"
            name="Impressions"
            fill={color}
            radius={[4, 4, 0, 0]}
          />

          {/* Line chart for Clicks */}
          {showClicks && (
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="clicks"
              name="Clicks"
              stroke={clicksColor}
              strokeWidth={3}
              strokeDasharray="5 5"
              dot={{ fill: clicksColor, r: 4 }}
              activeDot={{ r: 6 }}
            />
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}, (prevProps, nextProps) => {
  // Phase 1.3: Only re-render if data, color, or showClicks changed
  // Phase 2.1.3: Also check loading and error states
  return (
    JSON.stringify(prevProps.data) === JSON.stringify(nextProps.data) &&
    prevProps.color === nextProps.color &&
    prevProps.clicksColor === nextProps.clicksColor &&
    prevProps.showClicks === nextProps.showClicks &&
    prevProps.title === nextProps.title &&
    prevProps.isLoading === nextProps.isLoading &&
    prevProps.error === nextProps.error
  );
});

export default ComboChart;
